import { useState, useEffect } from 'react';
import { Save, Bell, Lock, User, CreditCard, Settings, RefreshCw, Mail, Clock, Globe } from 'lucide-react';
import { supabase } from '../services/supabase';
import { useSubscription } from '../hooks/useSubscription';
import { logger } from '../services/logger';
import { UserPreferences } from '../types';
import { TopicManager } from '../components/TopicManager';
import { CoachingOnboarding } from '../components/CoachingOnboarding';
import { ReferralDashboard } from '../components/ReferralDashboard';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time' },
  { value: 'Asia/Kolkata', label: 'India Standard Time' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

const REMINDER_TIMES = [
  { value: '06:00:00', label: '6:00 AM' },
  { value: '07:00:00', label: '7:00 AM' },
  { value: '08:00:00', label: '8:00 AM' },
  { value: '09:00:00', label: '9:00 AM' },
  { value: '12:00:00', label: '12:00 PM' },
  { value: '17:00:00', label: '5:00 PM' },
  { value: '18:00:00', label: '6:00 PM' },
  { value: '19:00:00', label: '7:00 PM' },
  { value: '20:00:00', label: '8:00 PM' },
  { value: '21:00:00', label: '9:00 PM' },
];

export function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTopicManager, setShowTopicManager] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [profile, setProfile] = useState({
    fullName: '',
    displayName: '',
    bio: '',
    company: '',
    position: '',
  });
  const [emailPrefs, setEmailPrefs] = useState({
    journalReminders: true,
    weeklyDigest: true,
    productUpdates: true,
    reminderTime: '20:00:00',
    timezone: 'America/New_York',
  });
  const [settings, setSettings] = useState<UserPreferences>({
    notifications: {
      email: true,
      push: true,
      dailyReminders: true,
      weeklyDigest: true
    },
    privacy: {
      profileVisibility: 'public',
      shareProgress: true,
      allowAiAnalysis: true
    },
    ai: {
      enhancedAnalysis: true,
      autoSuggestions: true,
      personalizedCoaching: true
    }
  });
  const { currentPlan, loading: subscriptionLoading } = useSubscription();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUserData();
  }, []);

  async function loadUserData() {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Load profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        throw profileError;
      }

      if (profileData) {
        setProfile({
          fullName: profileData.full_name || '',
          displayName: profileData.display_name || '',
          bio: profileData.bio || '',
          company: profileData.company || '',
          position: profileData.position || '',
        });

        setEmailPrefs({
          journalReminders: profileData.email_journal_reminders ?? true,
          weeklyDigest: profileData.email_weekly_digest ?? true,
          productUpdates: profileData.email_product_updates ?? true,
          reminderTime: profileData.reminder_time || '20:00:00',
          timezone: profileData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
        });
      }

      // Load settings
      const { data: userProgress, error: progressError } = await supabase
        .from('user_progress')
        .select('preferences')
        .eq('user_id', user.id)
        .single();

      if (progressError) {
        throw progressError;
      }

      if (userProgress?.preferences) {
        setSettings(prev => ({
          ...prev,
          ...(userProgress.preferences as UserPreferences)
        }));
      }
    } catch (err: any) {
      logger.error('Error loading user data:', err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelSubscription() {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    // Redirect to Stripe customer portal for subscription management
    window.open('https://billing.stripe.com/p/login/test_00000000000000', '_blank');
  }

  async function saveSettings() {
    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Update profile and email preferences
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: profile.fullName,
          display_name: profile.displayName,
          bio: profile.bio,
          company: profile.company,
          position: profile.position,
          email_journal_reminders: emailPrefs.journalReminders,
          email_weekly_digest: emailPrefs.weeklyDigest,
          email_product_updates: emailPrefs.productUpdates,
          reminder_time: emailPrefs.reminderTime,
          timezone: emailPrefs.timezone,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Update settings
      const { error: settingsError } = await supabase
        .from('user_progress')
        .upsert({
          user_id: user.id,
          preferences: settings,
          updated_at: new Date().toISOString()
        });

      if (settingsError) throw settingsError;

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg';
      successMessage.textContent = 'Settings saved successfully';
      document.body.appendChild(successMessage);
      setTimeout(() => document.body.removeChild(successMessage), 3000);

    } catch (err: any) {
      logger.error('Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your account and preferences</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="space-y-8">
        {/* Referral Program */}
        <ReferralDashboard />

        {/* Subscription Settings */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-6">
            <CreditCard className="h-5 w-5 text-gold-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
          </div>

          <div className="bg-gradient-to-br from-electric-400/5 to-gold-500/5 rounded-xl p-8 border border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Active Subscription</h3>
                <p className="text-gray-600 mt-1">Current Plan</p>
              </div>
              <div className="mt-4 md:mt-0 text-right">
                <div className="text-3xl font-bold text-gold-500">$9</div>
                <p className="text-sm text-gray-500">per month</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {[
                'Private journal with autosave',
                'Prompt library by life area',
                'Quick Read AI summaries',
                'Coach with skill packs',
                'Weekly Scorecard',
                'Export and delete anytime'
              ].map((feature, index) => (
                <div key={index} className="flex items-center text-sm text-gray-700">
                  <div className="w-1.5 h-1.5 bg-gold-500 rounded-full mr-2"></div>
                  {feature}
                </div>
              ))}
            </div>

            <div className="pt-6 border-t border-gray-200">
              {currentPlan !== 'basic' && (
                <button
                  onClick={handleCancelSubscription}
                  className="w-full text-center py-3 text-red-600 hover:text-red-700 transition-colors font-medium"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Profile Settings */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-6">
            <User className="h-5 w-5 text-gold-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Full Name</label>
              <input
                type="text"
                value={profile.fullName}
                onChange={(e) => setProfile(prev => ({ ...prev, fullName: e.target.value }))}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Display Name</label>
              <input
                type="text"
                value={profile.displayName}
                onChange={(e) => setProfile(prev => ({ ...prev, displayName: e.target.value }))}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Bio</label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                rows={3}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Company</label>
              <input
                type="text"
                value={profile.company}
                onChange={(e) => setProfile(prev => ({ ...prev, company: e.target.value }))}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Position</label>
              <input
                type="text"
                value={profile.position}
                onChange={(e) => setProfile(prev => ({ ...prev, position: e.target.value }))}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500"
              />
            </div>
          </div>
        </div>

        {/* Email Preferences */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-6">
            <Mail className="h-5 w-5 text-gold-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Email Preferences</h2>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Choose which emails you would like to receive from VZMO.
          </p>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-gray-900 font-medium">Daily Journal Reminders</label>
                <p className="text-sm text-gray-500">Get reminded to journal if you have not written today</p>
              </div>
              <button
                type="button"
                onClick={() => setEmailPrefs(prev => ({ ...prev, journalReminders: !prev.journalReminders }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  emailPrefs.journalReminders ? 'bg-gold-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailPrefs.journalReminders ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-gray-900 font-medium">Weekly Digest</label>
                <p className="text-sm text-gray-500">Receive a summary of your weekly activity and progress</p>
              </div>
              <button
                type="button"
                onClick={() => setEmailPrefs(prev => ({ ...prev, weeklyDigest: !prev.weeklyDigest }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  emailPrefs.weeklyDigest ? 'bg-gold-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailPrefs.weeklyDigest ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-gray-900 font-medium">Product Updates</label>
                <p className="text-sm text-gray-500">Learn about new features and improvements</p>
              </div>
              <button
                type="button"
                onClick={() => setEmailPrefs(prev => ({ ...prev, productUpdates: !prev.productUpdates }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  emailPrefs.productUpdates ? 'bg-gold-500' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailPrefs.productUpdates ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <Clock className="h-4 w-4 mr-1.5 text-gray-400" />
                    Reminder Time
                  </label>
                  <select
                    value={emailPrefs.reminderTime}
                    onChange={(e) => setEmailPrefs(prev => ({ ...prev, reminderTime: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 text-sm focus:border-gold-500 focus:ring-gold-500"
                  >
                    {REMINDER_TIMES.map(time => (
                      <option key={time.value} value={time.value}>{time.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <Globe className="h-4 w-4 mr-1.5 text-gray-400" />
                    Timezone
                  </label>
                  <select
                    value={emailPrefs.timezone}
                    onChange={(e) => setEmailPrefs(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 text-sm focus:border-gold-500 focus:ring-gold-500"
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-6">
            <Bell className="h-5 w-5 text-gold-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">In-App Notifications</h2>
          </div>
          <div className="space-y-4">
            {Object.entries(settings.notifications).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <label className="text-gray-700 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <div className="relative inline-block w-12 h-6">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      notifications: {
                        ...prev.notifications,
                        [key]: e.target.checked
                      }
                    }))}
                    className="sr-only"
                  />
                  <div className={`block w-12 h-6 rounded-full transition-colors ${
                    value ? 'bg-gold-500' : 'bg-gray-300'
                  }`} />
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${
                    value ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center mb-6">
            <Lock className="h-5 w-5 text-gold-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Privacy</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-gray-700">Profile Visibility</label>
              <select
                value={settings.privacy.profileVisibility}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  privacy: {
                    ...prev.privacy,
                    profileVisibility: e.target.value as 'public' | 'private' | 'friends'
                  }
                }))}
                className="rounded-lg border-gray-300 text-sm"
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="friends">Friends Only</option>
              </select>
            </div>
            {Object.entries(settings.privacy).map(([key, value]) => {
              if (key === 'profileVisibility') return null;
              return (
                <div key={key} className="flex items-center justify-between">
                  <label className="text-gray-700 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <div className="relative inline-block w-12 h-6">
                    <input
                      type="checkbox"
                      checked={Boolean(value)}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        privacy: {
                          ...prev.privacy,
                          [key]: e.target.checked
                        }
                      }))}
                      className="sr-only"
                    />
                    <div className={`block w-12 h-6 rounded-full transition-colors ${
                      value ? 'bg-indigo-600' : 'bg-gray-300'
                    }`} />
                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${
                      value ? 'translate-x-6' : 'translate-x-0'
                    }`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Coaching Topics */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Settings className="h-5 w-5 text-gold-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Coaching Topics</h2>
            </div>
            <button
              onClick={() => setShowTopicManager(true)}
              className="px-4 py-2 text-sm font-medium text-gold-500 hover:bg-gold-500/10 rounded-lg transition-colors"
            >
              Manage Topics
            </button>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Customize which coaching areas appear in your coaching hub. You can add or remove topics anytime based on what you're working on.
          </p>
          <button
            onClick={() => setShowOnboarding(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retake Onboarding Quiz
          </button>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center px-6 py-3 bg-gold-500 text-ink-950 rounded-lg hover:bg-gold-500/90 transition-colors disabled:opacity-50 font-semibold"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      {showTopicManager && (
        <TopicManager
          onClose={() => setShowTopicManager(false)}
          onSave={() => {
            setShowTopicManager(false);
            loadUserData();
          }}
        />
      )}

      {showOnboarding && (
        <CoachingOnboarding
          userName={profile.displayName || 'there'}
          onComplete={() => {
            setShowOnboarding(false);
            loadUserData();
          }}
          onSkip={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}